import {
  AI_DOCS_ACCEPTED_FILE_FORMATS,
  AI_DOCS_ACCEPTED_FILE_FORMATS_LABEL,
  DropzoneCustom,
  DropzoneFileItem,
  Button as DSButton,
  Typography,
} from "@platform/design-system";
import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CoverageTypeEnum } from "@platform/types";

import { DocumentType, UploadedDocument } from "../types";
import { useCoverageSummary, useDocumentFilters } from "../hooks";
import { DOCUMENT_TYPE_VALUES } from "../constants";
import { coverageTypeToLabel, documentTypeToLabel } from "../utils";

interface AddDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  files: DropzoneFileItem[];
  uploadedDocuments: UploadedDocument[];
  canUpload: boolean;
  carriers: string[];
  handleUploadFiles: (acceptedFiles: File[]) => void;
  handleDeleteFile: (fileToDelete: DropzoneFileItem) => void;
  handleDeleteDocument: (id: string) => void;
  handleDocumentChange: <K extends keyof UploadedDocument>(
    id: string,
    field: K,
    value: UploadedDocument[K],
  ) => void;
  handleUpload: () => void;
  isUploading: boolean;
}

const DOCUMENT_TYPES = DOCUMENT_TYPE_VALUES.map((value) => ({
  value,
  label: documentTypeToLabel(value),
}));

const MAX_FILE_SIZE_MB = 250; // 250 MB

const DOCUMENT_TABLE_HEADERS: { label: string; width?: string }[] = [
  { label: "Uploaded Documents", width: "28%" },
  { label: "Document Type", width: "20%" },
  { label: "Assigned Coverages", width: "22%" },
  { label: "Assigned Carrier", width: "22%" },
  { label: "", width: "7%" },
];

interface MenuOption {
  value: string;
  label: string;
}

interface MenuButtonProps {
  label: string;
  options: MenuOption[];
  selectedValue?: string | null;
  onSelect: (value: string) => void;
}

const TRUNCATED_TEXT_SX = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const BUTTON_LABEL_TRUNCATED_SX = {
  ...TRUNCATED_TEXT_SX,
  minWidth: 0,
  flex: 1,
  textAlign: "left",
};

interface TruncatedCellTextProps {
  text: string | null | undefined;
}

interface TruncatedButtonLabelProps {
  label: string;
  tooltipTitle?: React.ReactNode;
}

const TruncatedButtonLabel = ({
  label,
  tooltipTitle,
}: TruncatedButtonLabelProps): React.JSX.Element => (
  <Tooltip title={tooltipTitle ?? label}>
    <Typography
      component="span"
      variant="subtitle2"
      color="inherit"
      sx={BUTTON_LABEL_TRUNCATED_SX}
    >
      {label}
    </Typography>
  </Tooltip>
);

const TruncatedCellText = ({
  text,
}: TruncatedCellTextProps): React.JSX.Element | null => {
  if (!text) {
    return null;
  }

  return (
    <Tooltip title={text}>
      <Typography variant="body-medium" sx={TRUNCATED_TEXT_SX}>
        {text}
      </Typography>
    </Tooltip>
  );
};

const MenuButton = ({
  label,
  options,
  selectedValue,
  onSelect,
}: MenuButtonProps): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (): void => setAnchorEl(null);

  const handleSelect = (value: string): void => {
    onSelect(value);
    handleClose();
  };

  return (
    <>
      <DSButton
        size="sm"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        disableElevation
        variant="secondary"
        sx={{
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <TruncatedButtonLabel label={label} />
      </DSButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleSelect(opt.value)}
            selected={opt.value === selectedValue}
            sx={{ maxWidth: 320 }}
          >
            <Tooltip title={opt.label} placement="right">
              <Typography variant="body-medium" sx={TRUNCATED_TEXT_SX}>
                {opt.label}
              </Typography>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface CoverageSelectProps {
  doc: UploadedDocument;
  coverages: { value: CoverageTypeEnum; label: string; disable?: boolean }[];
  onCommit: (coverages: CoverageTypeEnum[]) => void;
}

const CoverageSelect = ({
  doc,
  coverages,
  onCommit,
}: CoverageSelectProps): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<CoverageTypeEnum[]>(
    doc.assignedCoverage ?? [],
  );

  useEffect(() => {
    const validCoverageTypes = new Set(
      coverages.map((coverage) => coverage.value),
    );
    setSelected((prev) =>
      prev.filter((coverage) => validCoverageTypes.has(coverage)),
    );
  }, [coverages]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    onCommit(selected);
  };

  const handleToggle = (value: CoverageTypeEnum): void => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const displayLabel =
    selected.length === 0
      ? "Assign Coverages"
      : selected.length === 1
        ? coverageTypeToLabel(selected[0])
        : `${selected.length} selected`;

  const selectedCoverageLabels = selected.map((coverage) =>
    coverageTypeToLabel(coverage),
  );

  const coverageTooltipTitle =
    selectedCoverageLabels.length <= 1
      ? displayLabel
      : selectedCoverageLabels.join(", ");

  return (
    <>
      <DSButton
        size="sm"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        disableElevation
        variant="secondary"
        sx={{
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <TruncatedButtonLabel
          label={displayLabel}
          tooltipTitle={coverageTooltipTitle}
        />
      </DSButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disablePortal
      >
        {coverages.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => {
              if (opt.disable) {
                return;
              }

              handleToggle(opt.value);
            }}
            disabled={opt.disable ?? false}
            selected={selected.includes(opt.value)}
            sx={{ maxWidth: 320 }}
            title={opt.label}
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              size="small"
              disableRipple
              disabled={opt.disable ?? false}
            />
            <ListItemText
              primary={
                <Tooltip title={opt.label} placement="right">
                  <Typography variant="body-medium" sx={TRUNCATED_TEXT_SX}>
                    {opt.label}
                  </Typography>
                </Tooltip>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface DocumentRowProps {
  doc: UploadedDocument;
  coverages: { value: CoverageTypeEnum; label: string; disable?: boolean }[];
  carriers: string[];
  handleDocumentChange: <K extends keyof UploadedDocument>(
    id: string,
    field: K,
    value: UploadedDocument[K],
  ) => void;
  handleDeleteDocument: (id: string) => void;
}

const DocumentRow = ({
  doc,
  coverages,
  carriers,
  handleDocumentChange,
  handleDeleteDocument,
}: DocumentRowProps): React.JSX.Element => (
  <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
    <TableCell sx={{ py: 1.5 }}>
      <TruncatedCellText text={doc.name} />
    </TableCell>

    <TableCell sx={{ py: 1, px: 1 }}>
      <MenuButton
        label={
          doc.documentType ? documentTypeToLabel(doc.documentType) : "Doc Type"
        }
        options={DOCUMENT_TYPES}
        selectedValue={doc.documentType}
        onSelect={(v) =>
          handleDocumentChange(doc.id, "documentType", v as DocumentType)
        }
      />
    </TableCell>

    <TableCell sx={{ py: 1, px: 1 }}>
      <CoverageSelect
        doc={doc}
        coverages={coverages}
        onCommit={(coverages) =>
          handleDocumentChange(doc.id, "assignedCoverage", coverages)
        }
      />
    </TableCell>

    <TableCell sx={{ py: 1, px: 1 }}>
      <MenuButton
        label={doc.assignedCarrier ?? "Assign Carrier"}
        options={[
          { value: "other", label: "Other" },
          ...carriers.map((carrier) => ({ value: carrier, label: carrier })),
        ]}
        selectedValue={doc.assignedCarrier}
        onSelect={(v) => handleDocumentChange(doc.id, "assignedCarrier", v)}
      />
    </TableCell>

    <TableCell sx={{ py: 1, pr: 0.5, width: "48px", textAlign: "right" }}>
      <IconButton
        size="small"
        color="brandPrimary"
        onClick={() => handleDeleteDocument(doc.id)}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </TableCell>
  </TableRow>
);

export const AddDocumentsDialog = ({
  open,
  onClose,
  files,
  uploadedDocuments,
  canUpload,
  carriers,
  handleUploadFiles,
  handleDeleteFile,
  handleDeleteDocument,
  handleDocumentChange,
  handleUpload,
  isUploading,
}: AddDocumentsDialogProps): React.JSX.Element => {
  const { coverageOptions } = useDocumentFilters();
  const { coverages: allCoverages } = useCoverageSummary();

  const currentCoverageTypes = useMemo(
    () =>
      new Set(
        allCoverages.filter((c) => c.isCurrent).map((c) => c.coverageType),
      ),
    [allCoverages],
  );

  const coverages = useMemo(
    () =>
      coverageOptions
        .filter(
          (coverage) =>
            Object.values(CoverageTypeEnum).includes(
              coverage.value as CoverageTypeEnum,
            ) && currentCoverageTypes.has(coverage.value as CoverageTypeEnum),
        )
        .map((coverage) => ({
          value: coverage.value as CoverageTypeEnum,
          label: coverage.label,
          disable: coverage.disable,
        })),
    [coverageOptions, currentCoverageTypes],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Document Upload</DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <DropzoneCustom
          files={files}
          handleUploadFiles={handleUploadFiles}
          onFileDelete={handleDeleteFile}
          maxMbSize={MAX_FILE_SIZE_MB}
          acceptedFormatFiles={AI_DOCS_ACCEPTED_FILE_FORMATS}
          allowedFormatLabel={AI_DOCS_ACCEPTED_FILE_FORMATS_LABEL}
          isMultiple
          showFiles={false}
        />

        <TableContainer
          sx={{
            mt: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "surfaces.primary" }}>
                {DOCUMENT_TABLE_HEADERS.map((header) => (
                  <TableCell
                    key={header.label}
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      py: 1.5,
                      width: header.width,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {uploadedDocuments.length > 0 ? (
                uploadedDocuments.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    coverages={coverages}
                    carriers={carriers}
                    handleDocumentChange={handleDocumentChange}
                    handleDeleteDocument={handleDeleteDocument}
                  />
                ))
              ) : (
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    colSpan={DOCUMENT_TABLE_HEADERS.length}
                    sx={{
                      py: 3,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    Add files above to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <DSButton variant="tertiary" onClick={onClose} disabled={isUploading}>
          Cancel
        </DSButton>
        <DSButton
          variant="primary"
          disabled={!canUpload}
          onClick={handleUpload}
          loading={isUploading}
        >
          Upload
        </DSButton>
      </DialogActions>
    </Dialog>
  );
};
